/**
 * Capability metrics for the Expertise view.
 *
 * The old view answered "does this tag exist". The mockup answers the two questions
 * that make the view worth showing leadership: **who can staff this** and **where
 * are we thin**. Both need numbers, not a tag cloud, so the arithmetic lives here
 * where it can be tested.
 *
 * "Single point of failure" is the uncomfortable one and the reason this view has a
 * claim on anyone's attention: a skill exactly one person holds is a scheduling and
 * succession risk that nothing else in the app surfaces.
 */

/** Skills held by exactly one person — the risk list. */
export const SINGLE_POINT_THRESHOLD = 1;

/** Skills held by 1–2 people get a risk pill rather than looking like any other. */
export const THIN_THRESHOLD = 2;

/**
 * @param {Array<{skill: string, count: number, people?: Array}>} allSkills
 * @param {Array<object>} employees
 */
export function expertiseKpis(allSkills = [], employees = [], totalTagCount = null) {
  const covered = allSkills.filter((s) => s.count > 0);
  const uncovered = allSkills.filter((s) => !s.count);

  const taggedPeople = new Set();
  for (const entry of covered) {
    for (const person of entry.people ?? []) {
      taggedPeople.add(person.id ?? person);
    }
  }

  const deepest = covered.reduce(
    (best, s) => (s.count > (best?.count ?? 0) ? s : best),
    null,
  );

  const singlePoints = covered.filter((s) => s.count === SINGLE_POINT_THRESHOLD);

  return {
    totalTags: totalTagCount ?? allSkills.length,
    coveredCount: covered.length,
    uncoveredCount: uncovered.length,
    taggedPeopleCount: taggedPeople.size,
    untaggedPeopleCount: Math.max(0, employees.length - taggedPeople.size),
    taggedPercent: employees.length
      ? Math.round((taggedPeople.size / employees.length) * 100)
      : 0,
    deepest,
    singlePointCount: singlePoints.length,
    singlePoints,
  };
}

/**
 * Bar width as a percentage of the deepest skill, so magnitude is comparative
 * rather than absolute — the mockup scales every bar against BIM/Revit at 96.
 */
export function coveragePercent(count, maxCount) {
  if (!count || !maxCount) return 0;
  return Math.max(4, Math.round((count / maxCount) * 100));
}

export const SKILL_SORTS = {
  MOST: 'most',
  THINNEST: 'thinnest',
  ALPHA: 'alpha',
};

/**
 * Thinnest sorts ascending but keeps zero-holder tags out of the way — a tag
 * nobody has is a gap, not a thin capability, and they are a separate tab.
 */
export function sortSkills(skills = [], mode = SKILL_SORTS.MOST) {
  const copy = [...skills];
  switch (mode) {
    case SKILL_SORTS.THINNEST:
      return copy.sort((a, b) => {
        if (!a.count !== !b.count) return a.count ? -1 : 1;
        return a.count - b.count || a.skill.localeCompare(b.skill);
      });
    case SKILL_SORTS.ALPHA:
      return copy.sort((a, b) => a.skill.localeCompare(b.skill));
    default:
      return copy.sort((a, b) => b.count - a.count || a.skill.localeCompare(b.skill));
  }
}

/**
 * Per-category coverage, for the rail's "7/10" counts. A category where most tags
 * are unclaimed is the actual finding, and the old view could not express it.
 */
export function categoryCoverage(categories = [], countsByTag = new Map()) {
  return categories.map((category) => {
    const tags = category.tags ?? [];
    const covered = tags.filter((tag) => (countsByTag.get(tag) || 0) > 0).length;
    return {
      ...category,
      coveredCount: covered,
      totalCount: tags.length,
      uncoveredCount: tags.length - covered,
    };
  });
}

/**
 * Coverage map — mockup 1D. Top capabilities × the biggest offices, plus an
 * "Other N" column so the row totals still add up to the real headcount.
 *
 * This is the one view that answers "can the Reno office run this pursuit on its
 * own", which neither a per-skill count nor a per-office count can answer alone.
 *
 * Heat is bucketed rather than continuous: five steps a reader can name, with the
 * single-point-of-failure cell flagged separately so this tab and the risk list
 * cannot disagree.
 *
 * @param {Array<{skill: string, count: number, byOffice?: Map<string, number>}>} allSkills
 * @param {Array<object>} employees
 * @param {{skillLimit?: number, officeLimit?: number}} [options]
 */
export function coverageMatrix(allSkills = [], employees = [], options = {}) {
  const { skillLimit = 8, officeLimit = 5 } = options;

  const headcountByOffice = new Map();
  for (const employee of employees) {
    if (!employee.officeLocation) continue;
    headcountByOffice.set(
      employee.officeLocation,
      (headcountByOffice.get(employee.officeLocation) ?? 0) + 1,
    );
  }

  const offices = [...headcountByOffice.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, officeLimit)
    .map(([office]) => office);

  const otherOfficeCount = Math.max(0, headcountByOffice.size - offices.length);

  const skills = allSkills
    .filter((entry) => entry.count > 0)
    .sort((a, b) => b.count - a.count || a.skill.localeCompare(b.skill))
    .slice(0, skillLimit);

  const rows = skills.map((entry) => {
    const byOffice = entry.byOffice ?? new Map();
    const named = offices.map((office) => byOffice.get(office) ?? 0);
    // Everything not in a named column, INCLUDING holders with no office on
    // record — otherwise the row would silently lose people.
    const other = entry.count - named.reduce((sum, n) => sum + n, 0);
    return {
      skill: entry.skill,
      total: entry.count,
      cells: [...named, Math.max(0, other)],
    };
  });

  return { offices, otherOfficeCount, rows };
}

/** Five namable steps, scaled against the busiest cell in the matrix. */
export function heatLevel(count, maxCount) {
  if (!count) return 0;
  if (!maxCount || maxCount <= 0) return 1;
  const share = count / maxCount;
  if (share >= 0.66) return 4;
  if (share >= 0.4) return 3;
  if (share >= 0.18) return 2;
  return 1;
}
